import mixpanel from 'mixpanel-browser';
import { reportTracking } from "../util/api";
import { getDeviceId, getSessionId, getSessionUser } from "./analytics";


const STORAGE_KEY = "lastTrackedPath";

export class LoggingAnalyticsHandler {
  constructor() {
    this.lastTrackedPath = null;
    this.trackingInProgress = false;
  }

  trackPageView(url) {
    // Prevent duplicate tracking for the same path
    if (this.lastTrackedPath === url) {
      return;
    }

    // Prevent rapid successive tracking calls
    if (this.trackingInProgress) {
      return;
    }

    this.lastTrackedPath = url;
    this.trackingInProgress = true;

    const { userId, userType, userEmail } = getSessionUser();
    const deviceId = getDeviceId();

    reportTracking({
      eventType: "page_visit",
      path: url,
      vistitedAt: new Date().toString(),
      sessionId: getSessionId(),
      userId,
      userType,
      userEmail,
      deviceId
    }).finally(() => {
      this.trackingInProgress = false;
    });
  }
}

// Google Analytics 4 (GA4) using gtag.js script, which is included in util/includeScripts.js
export class GoogleAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    // GA4 property. Manually send page_view events
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    // Note 1: You should turn "Enhanced measurement" off.
    //         It attaches own listeners to elements and that breaks in-app navigation.
    // Note 2: If previousPath is null (just after page load), gtag script sends page_view event automatically.
    //         Only in-app navigation needs to be sent manually from SPA.
    // Note 3: Timeout is needed because gtag script picks up <title>,
    //         and location change event happens before initial rendering.
    if (previousPath && window.gtag) {
      window.setTimeout(() => {
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
        });
      }, 300);
    }
  }
}

export const getFriendlyPageName = path => {
  if (!path) return 'Unknown Page';

  // 1. Signup / Entry points
  if (path.startsWith('/signup')) {
    return 'Sign up';
  }
  if (path.startsWith('/step1')) {
    return 'Sign up - filling profile step 1';
  }
  if (path.startsWith('/step2')) {
    return 'Sign up - filling profile step 2';
  }
  if (path.startsWith('/confirm')) {
    return 'Confirm Email';
  }

  // 2. Fractional wizard tabs
  if (path.includes('/details') || path.endsWith('/l/new')) {
    return 'The Job';
  }
  if (path.includes('/extra-detail')) return 'How I Do';
  if (path.includes('/pricing')) return 'What I Earn';
  if (path.includes('/location')) return 'What I’ve Learned';
  if (path.includes('/past-efforts')) return 'What I’ve Done';
  if (path.includes('/verfiy-yourself')) return 'Verify Yourself';
  if (path.includes('/photos')) return 'Selfies';

  // 3. Company wizard tabs
  if (path.endsWith('/l/newCompany') || path.includes('/requirements')) return 'What you Need';
  if (path.includes('/how')) return 'How They’ll do It';
  if (path.includes('/what')) return 'What you’ll pay';
  if (path.includes('/where')) return 'Where They’ll Work';

  return path;
};

export class MixpanelSessionHandler {
  constructor() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Set up the interval flush every 1 hour (3600000 ms) instead of 10 minutes
    this.intervalId = setInterval(() => {
      this.flushSessionActivity();
    }, 60 * 60 * 1000);

    // Initialize visibility state based on current document visibility
    this.isActive = document.visibilityState === 'visible';

    // Set up the pagehide/beforeunload listeners for exit flush
    this.handlePageHide = () => {
      this.flushSessionActivity(true);
    };

    // Set up tab focus/visibility change listeners
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.pauseTracking();
      } else {
        this.resumeTracking();
      }
    };

    this.handleBlur = () => {
      this.pauseTracking();
    };

    this.handleFocus = () => {
      this.resumeTracking();
    };

    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('beforeunload', this.handlePageHide);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('focus', this.handleFocus);
  }

  destroy() {
    if (typeof window === 'undefined') return;
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('beforeunload', this.handlePageHide);
    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
    window.removeEventListener('blur', this.handleBlur);
    window.removeEventListener('focus', this.handleFocus);
  }

  pauseTracking() {
    if (!this.isActive) return;
    this.isActive = false;

    const now = Date.now();
    const currentPage = sessionStorage.getItem('mixpanel_current_page');
    const pageStartTimeStr = sessionStorage.getItem('mixpanel_page_start_time');

    if (currentPage && pageStartTimeStr) {
      const pageStartTime = parseInt(pageStartTimeStr, 10);
      const duration = Math.round((now - pageStartTime) / 1000);
      if (duration >= 0) {
        const accumulatedStr =
          sessionStorage.getItem('mixpanel_page_accumulated_duration') || '0';
        const accumulated = parseInt(accumulatedStr, 10) + duration;
        sessionStorage.setItem(
          'mixpanel_page_accumulated_duration',
          accumulated.toString()
        );
      }
    }
    sessionStorage.removeItem('mixpanel_page_start_time');
  }

  resumeTracking() {
    if (this.isActive) return;
    this.isActive = true;

    const now = Date.now();
    sessionStorage.setItem('mixpanel_page_start_time', now.toString());
  }

  trackPageView(canonicalPath) {
    if (typeof window === 'undefined') return;

    const now = Date.now();
    let currentPage = sessionStorage.getItem('mixpanel_current_page');
    let pageStartTimeStr = sessionStorage.getItem('mixpanel_page_start_time');
    let pagesStr = sessionStorage.getItem('mixpanel_batch_pages');
    let pages = pagesStr ? JSON.parse(pagesStr) : [];

    // Ignore duplicate trackPageView calls for the exact same page
    if (currentPage === canonicalPath) {
      return;
    }

    // Record the time spent on the previous page
    if (currentPage) {
      let accumulatedStr =
        sessionStorage.getItem('mixpanel_page_accumulated_duration') || '0';
      let totalDuration = parseInt(accumulatedStr, 10);

      if (pageStartTimeStr) {
        const pageStartTime = parseInt(pageStartTimeStr, 10);
        const duration = Math.round((now - pageStartTime) / 1000);
        if (duration >= 0) {
          totalDuration += duration;
        }
      }

      if (totalDuration >= 0) {
        pages.push({
          path: currentPage,
          name: getFriendlyPageName(currentPage),
          duration: totalDuration,
        });
      }
    }

    // Update state for the new page
    sessionStorage.setItem('mixpanel_current_page', canonicalPath);
    if (this.isActive) {
      sessionStorage.setItem('mixpanel_page_start_time', now.toString());
    } else {
      sessionStorage.removeItem('mixpanel_page_start_time');
    }
    sessionStorage.setItem('mixpanel_page_accumulated_duration', '0');
    sessionStorage.setItem('mixpanel_batch_pages', JSON.stringify(pages));

    // Ensure we have a batch start time
    if (!sessionStorage.getItem('mixpanel_batch_start_time')) {
      sessionStorage.setItem('mixpanel_batch_start_time', now.toString());
    }

    const friendlyName = getFriendlyPageName(canonicalPath);
    const { userId, userType } = getSessionUser();
    const sessionId = getSessionId();

    mixpanel.track('Page Visit', {
      page_name: friendlyName,
      canonical_path: canonicalPath,
      session_id: sessionId,
      user_id: userId,
      user_type: userType,
    });

    const ONBOARDING_STEPS = [
      'The Job',
      'How I Do',
      'What I Earn',
      'What I’ve Learned',
      'What I’ve Done',
      'Selfies',
      'Verify Yourself',
      'What you Need',
      'How They’ll do It',
      'What you’ll pay',
      'Where They’ll Work',
    ];

    if (ONBOARDING_STEPS.includes(friendlyName)) {
      const isCompanyStep = ['What you Need', 'How They’ll do It', 'What you’ll pay', 'Where They’ll Work'].includes(friendlyName);
      mixpanel.track('Onboarding Step Viewed', {
        step_name: friendlyName,
        user_type: userType || (isCompanyStep ? 'company' : 'fractionalPro'),
        canonical_path: canonicalPath,
        session_id: sessionId,
      });
    }
  }

  flushSessionActivity(isExit = false) {
    if (typeof window === 'undefined') return;

    const now = Date.now();
    let currentPage = sessionStorage.getItem('mixpanel_current_page');
    let pageStartTimeStr = sessionStorage.getItem('mixpanel_page_start_time');
    let pagesStr = sessionStorage.getItem('mixpanel_batch_pages');
    let pages = pagesStr ? JSON.parse(pagesStr) : [];
    let batchStartTimeStr = sessionStorage.getItem('mixpanel_batch_start_time');
    let batchStartTime = batchStartTimeStr ? parseInt(batchStartTimeStr, 10) : now;

    const pagesWithCurrent = [...pages];
    if (currentPage) {
      let accumulatedStr =
        sessionStorage.getItem('mixpanel_page_accumulated_duration') || '0';
      let totalDuration = parseInt(accumulatedStr, 10);

      if (pageStartTimeStr) {
        const pageStartTime = parseInt(pageStartTimeStr, 10);
        const duration = Math.round((now - pageStartTime) / 1000);
        if (duration >= 0) {
          totalDuration += duration;
        }
      }

      if (totalDuration >= 0) {
        pagesWithCurrent.push({
          path: currentPage,
          name: getFriendlyPageName(currentPage),
          duration: totalDuration,
        });
      }
    }

    if (pagesWithCurrent.length === 0) {
      return;
    }

    const totalActiveTime = pagesWithCurrent.reduce(
      (sum, p) => sum + p.duration,
      0
    );
    const uniquePaths = Array.from(new Set(pagesWithCurrent.map(p => p.path)));

    const trackOptions = isExit ? { transport: 'sendBeacon' } : {};

    mixpanel.track('User Session Activity', {
      session_id: getSessionId(),
      pages: pagesWithCurrent,
      total_active_time: totalActiveTime,
      unique_pages: uniquePaths.length,
      is_bounce: uniquePaths.length === 1,
      batch_started_at: new Date(batchStartTime).toISOString(),
      batch_ended_at: new Date(now).toISOString(),
      batch_duration_seconds: Math.round((now - batchStartTime) / 1000)
    }, trackOptions);

    // Reset tracking state
    sessionStorage.setItem('mixpanel_batch_pages', JSON.stringify([]));
    sessionStorage.setItem('mixpanel_batch_start_time', now.toString());

    if (currentPage && !isExit) {
      if (this.isActive) {
        sessionStorage.setItem('mixpanel_page_start_time', now.toString());
      } else {
        sessionStorage.removeItem('mixpanel_page_start_time');
      }
      sessionStorage.setItem('mixpanel_page_accumulated_duration', '0');
    } else if (isExit) {
      sessionStorage.removeItem('mixpanel_current_page');
      sessionStorage.removeItem('mixpanel_page_start_time');
      sessionStorage.removeItem('mixpanel_page_accumulated_duration');
      sessionStorage.removeItem('mixpanel_batch_start_time');
    }
  }
}
